/**
 * BossCutIn.js
 * Implements the Boss cut-in
 */

import { Game, GameTimer, GameTween, GameWindow, UiElement, UiImage, UiText, WindowLayers } from "pixi_core";
import { Enemy } from "../Enemies/Enemy";
import { Common } from "../game/Common";

import {
    BossAllOutAttackSprite,
    GameEvents,
    EnemyAbilities,
    ENEMY_ABILITY_NAMES,
    ABILITY_NAME_ROTATION,
    TEMP_CUT_OUT_TEXT_DISTANCE_MULTIPLIER
} from "../Constants";

export class BossCutIn extends GameWindow {
    /**
     * @public
     */
    constructor() {
        super({
            name: "BossCutIn",
            disableInputUnder: false,
            showDarkenUnder: false,
            ignoreOutsideZoneTap: true,
            ignoreGearButton: true,
            ignoreAndroidBackButton: true,
            defaultInstantClose: true,
            defaultInstantOpen: true,
            layer: WindowLayers.eUNDER_GEAR
        });

        /** @private @type {UiElement} */
        this._ui = null;

        /** @private @type {Enemy} */
        this._boss = null;

        /** @private @type {EnemyAbilities} */
        this._abilityId = "";

        /** @private @type {UiImage} */
        this._abilityCutIn = null;

        /** @private @type {UiImage} */
        this._abilityTextCutIn = null;

        /** @private @type {UiText} */
        this._lblCutInAbility = null;

        /** @private @type {Object} */
        this._cutInFinalPos = { x: 0, y: 0 };

        /** @private @type {Object} */
        this._cutInTextMiddlePos = { x: 0, y: 0 };

        /** @private @type {Object} */
        this._cutInStartingPos = { x: 0, y: 0 };

        /** @private @type {Object} */
        this._cutInTextStartingPos = { x: 0, y: 0 };

        /** @private @type {Object} */
        this._cutInTextFinalPos = { x: 0, y: 0 };

        /** @private @type {GameTween} */
        this._tweenBossCutIn = null;

        /** @private @type {GameTween} */
        this._tweenTextCutIn = null;

        /** @private @type {GameTimer} */
        this._timerBossCutStop = null;

        /** @private @type {GameTween} */
        this._tweenBossCutOut = null;

        /** @private @type {GameTween} */
        this._tweenTextCutOut = null;

        Game.on(GameEvents.eBOSS_SET_CUTIN, this._onBossSetCutIn, this);
        Game.on(GameEvents.eATTACKPHASE_CUTIN_BEGIN, this._onBeginCutIn, this);
    }

    /**
     * The window is opening for the first time
     * @protected
     */
    _onFirstWindowOpening() {
        this._ui = Game.ui.instantiate("BossCutIns", this);

        this._abilityCutIn = this._ui.findElementByName("AbilityCutIn");
        this._abilityTextCutIn = this._ui.findElementByName("ImgCutInName");
        this._lblCutInAbility = this._ui.findElementByName("LblAbility");
        this._lblCutInAbility.setLocalized(true);
        this._lblCutInAbility.uiRoot.angle = ABILITY_NAME_ROTATION;

        this._cutInFinalPos = { x: this._abilityCutIn.x, y: this._abilityCutIn.y };
        this._cutInTextMiddlePos = { x: this._abilityTextCutIn.x, y: this._abilityTextCutIn.y };
        this._cutInStartingPos = {
            x: this._abilityCutIn.x + this._abilityCutIn.width,
            y: this._abilityCutIn.y - Common.settings.cutInYDistance
        };
        this._cutInTextStartingPos = {
            x: this._abilityTextCutIn.x + this._abilityCutIn.width,
            y: this._abilityTextCutIn.y - Common.settings.cutInYDistance
        };
        this._cutInTextFinalPos = {
            x: this._abilityTextCutIn.x - Common.settings.cutInXDistance * TEMP_CUT_OUT_TEXT_DISTANCE_MULTIPLIER,
            y: this._abilityTextCutIn.y + Common.settings.cutInYDistance * TEMP_CUT_OUT_TEXT_DISTANCE_MULTIPLIER
        };
    }

    /**
     * The window is opening
     * @protected
     * @param {Object} options
     */
    _onWindowOpening(options) {}

    /**
     * The window is opened
     * @protected
     */
    _onWindowOpened() {}

    /**
     * The window is closing
     * @protected
     */
    _onWindowClosing(options) {}

    /**
     * The window is closed
     * @protected
     */
    _onWindowClosed() {
        this._destroyCutInTweens();
    }

    /**
     * @private
     * @param {Enemy} boss
     */
    _onBossSetCutIn(boss) {
        this._boss = boss;
    }

    /**
     * @private
     * @param {EnemyAbilities} abilityId
     */
    _onBeginCutIn(abilityId) {
        this.open();

        Game.audio.playSfx("IDSND_CRT_CUT_IN_APPEAR");

        this._abilityCutIn.setSprite(BossAllOutAttackSprite[this._boss.name]);
        this._abilityCutIn.visible = true;
        this._abilityTextCutIn.visible = true;
        this._abilityId = abilityId;
        this._lblCutInAbility.text = ENEMY_ABILITY_NAMES[this._abilityId];
        this._abilityCutIn.setPosition(this._cutInStartingPos.x, this._cutInStartingPos.y);
        this._abilityTextCutIn.setPosition(this._cutInTextStartingPos.x, this._cutInTextStartingPos.y);

        this._tweenBossCutIn = Game.windows
            .getCurrentWindow()
            .createTween(this._abilityCutIn)
            .to({ x: this._cutInFinalPos.x, y: this._cutInFinalPos.y }, Common.settings.cutInMoveDuration)
            .easing(GameTween.Easing.QuadraticOut)
            .onComplete(() => {
                this._timerBossCutStop.start();
            });

        this._tweenTextCutIn = Game.windows
            .getCurrentWindow()
            .createTween(this._abilityTextCutIn)
            .to({ x: this._cutInTextMiddlePos.x, y: this._cutInTextMiddlePos.y }, Common.settings.cutInMoveDuration)
            .easing(GameTween.Easing.QuadraticOut)
            .onComplete(() => {});

        this._timerBossCutStop = Game.windows.getCurrentWindow().createTimer(Common.settings.cutInStopDuration, { autoStart: false });
        this._timerBossCutStop.onComplete = () => {
            this._tweenBossCutOut.start();
            this._tweenTextCutOut.start();
        };

        this._tweenBossCutOut = Game.windows
            .getCurrentWindow()
            .createTween(this._abilityCutIn)
            .to({ x: this._cutInStartingPos.x, y: this._cutInStartingPos.y }, Common.settings.cutInMoveDuration)
            .easing(GameTween.Easing.QuadraticIn)
            .onComplete(() => {
                this._onCutInComplete();
            });

        this._tweenTextCutOut = Game.windows
            .getCurrentWindow()
            .createTween(this._abilityTextCutIn)
            .to({ x: this._cutInTextFinalPos.x, y: this._cutInTextFinalPos.y }, Common.settings.cutInMoveDuration)
            .easing(GameTween.Easing.QuadraticIn)
            .onComplete(() => {});

        this._tweenBossCutIn.start();
        this._tweenTextCutIn.start();
    }

    /**
     * @private
     */
    _onCutInComplete() {
        this._abilityCutIn.visible = false;
        this._abilityTextCutIn.visible = false;

        this._destroyCutInTweens();
        this.close();

        Game.fire(GameEvents.eATTACKPHASE_ABILITY_BEGIN, this._boss, this._abilityId);
        Game.fire(GameEvents.eBOSS_CUT_IN_DONE, this._boss);
    }

    /**
     * @private
     */
    _destroyCutInTweens() {
        if (this._tweenBossCutIn) {
            this._tweenBossCutIn.destroy();
            this._tweenBossCutIn = null;
        }

        if (this._timerBossCutStop) {
            this._timerBossCutStop.destroy();
            this._timerBossCutStop = null;
        }

        if (this._tweenBossCutOut) {
            this._tweenBossCutOut.destroy();
            this._tweenBossCutOut = null;
        }

        if (this._tweenTextCutIn) {
            this._tweenTextCutIn.destroy();
            this._tweenTextCutIn = null;
        }

        if (this._tweenTextCutOut) {
            this._tweenTextCutOut.destroy();
            this._tweenTextCutOut = null;
        }
    }
}
